/**
 * Profile photo editor — separate loadout from play cosmetics.
 * Inventory-style cards; Cursor category is exclusive and always bottom layer.
 */
import { apiPath } from "../lib/apiPath";
import {
  PROFILE_PHOTO_MAX_ITEMS,
  PROFILE_PHOTO_ZOOM_MAX,
  PROFILE_PHOTO_ZOOM_MIN,
  PROFILE_PHOTO_BG_DEFAULT,
  normalizeProfilePhotoConfig,
  normalizeProfilePhotoBackgroundColor,
  profilePhotoCosmeticIds,
  type ProfilePhotoConfig,
} from "../../lib/profilePhoto";
import {
  cosmeticCardCursorRadius,
  drawCosmeticOverlay,
  getCachedCosmeticImage,
  loadCosmeticImage,
} from "./cursor-cosmetics";
import { startCosmeticPreviewLoop, stopCosmeticPreviewLoop } from "./cosmetic-preview-loop";
import { ANIM_FRAME_MS } from "./shop-creator-fx";
import {
  paintProfilePhoto,
  resolveBannerDominantBackground,
  type ProfileShowcaseCosmetic,
} from "./profile-showcase";

export type ProfilePhotoEditorOpts = {
  cosmetics: ProfileShowcaseCosmetic[];
  cursorColor: string;
  initial: ProfilePhotoConfig | null;
  resolveCosmeticDisplay: (
    item: ProfileShowcaseCosmetic,
    cursorColor: string,
  ) => Promise<{ imageData: string; animationFrames: string[] | null }>;
  onSaved: (photo: ProfilePhotoConfig | null) => void;
};

const CARD_PREVIEW_SIZE = 160;
const CARD_FILL_RATIO = 1.22;

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

function isCursorCategory(c: ProfileShowcaseCosmetic | undefined): boolean {
  return (c?.category || "") === "cursor";
}

function paintEditorCard(
  canvas: HTMLCanvasElement,
  imageData: string,
  animationFrames: string[] | null | undefined,
  cursorColor: string,
  hideBase: boolean,
  animate: boolean,
) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const cx = w * 0.5;
  const cy = h * 0.5;
  const r = cosmeticCardCursorRadius(Math.min(w, h), CARD_FILL_RATIO);
  if (!hideBase) {
    ctx.fillStyle = cursorColor || "#ffffff";
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(imageData));
  if (animationFrames?.length) {
    const fi = animate
      ? Math.floor(performance.now() / ANIM_FRAME_MS) % animationFrames.length
      : 0;
    drawCosmeticOverlay(ctx, cx, cy, r, getCachedCosmeticImage(animationFrames[fi]));
  }
}

export function openProfilePhotoEditor(opts: ProfilePhotoEditorOpts) {
  const cursorCosmetics = opts.cosmetics.filter(
    (c) => c.category !== "banner" && c.category !== "trail",
  );
  const byId = new Map(cursorCosmetics.map((c) => [c.id, c]));

  const initialIds = profilePhotoCosmeticIds(opts.initial).filter((id) => byId.has(id));

  let draft: ProfilePhotoConfig = normalizeProfilePhotoConfig({
    ...(opts.initial || {}),
    cosmeticIds: initialIds.length
      ? initialIds
      : cursorCosmetics[0]
        ? [cursorCosmetics[0].id]
        : [],
    zoom: opts.initial?.zoom ?? 1,
    rotation: opts.initial?.rotation ?? 0,
    offsetX: opts.initial?.offsetX ?? 0,
    offsetY: opts.initial?.offsetY ?? 0,
    backgroundColor: opts.initial?.backgroundColor ?? PROFILE_PHOTO_BG_DEFAULT,
    backgroundFromBanner: opts.initial?.backgroundFromBanner === true,
  }) || {
    cosmeticIds: cursorCosmetics[0] ? [cursorCosmetics[0].id] : [],
    cosmeticId: cursorCosmetics[0]?.id,
    zoom: 1,
    rotation: 0,
    offsetX: 0,
    offsetY: 0,
    backgroundColor: PROFILE_PHOTO_BG_DEFAULT,
    backgroundFromBanner: false,
  };

  const modal = document.createElement("div");
  modal.className = "profilePhotoEditorModal screen";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Edit profile photo");

  const panel = document.createElement("div");
  panel.className = "modeDetailModalPanel modeBox profilePhotoEditorPanel";

  const title = document.createElement("h2");
  title.textContent = "Profile photo";
  panel.appendChild(title);

  const hint = document.createElement("p");
  hint.className = "accessHint";
  hint.textContent =
    "Pick cosmetics as cards (like Inventory). Cursor items: only one, always the bottom layer. Set a background color, or sync it from your equipped banner’s most common color.";
  panel.appendChild(hint);

  const frame = document.createElement("div");
  frame.className = "profilePhotoEditorFrame";
  const canvas = document.createElement("canvas");
  canvas.className = "profilePhotoEditorCanvas";
  canvas.width = 220;
  canvas.height = 220;
  frame.appendChild(canvas);
  panel.appendChild(frame);

  const grid = document.createElement("div");
  grid.className = "profilePhotoEditorCardGrid";
  grid.setAttribute("role", "group");
  grid.setAttribute("aria-label", "Photo cosmetics");

  const selected = new Set(draft.cosmeticIds);
  /** Stack order: first = bottom. Cursor category always forced to index 0. */
  let order = [...draft.cosmeticIds];

  const enforceCursorRules = (ids: string[]): string[] => {
    const cursors = ids.filter((id) => isCursorCategory(byId.get(id)));
    const others = ids.filter((id) => !isCursorCategory(byId.get(id)));
    if (cursors.length <= 1) return cursors.length ? [cursors[0], ...others] : others;
    // Keep the first cursor in current order; drop extras from selection.
    const keep = cursors[0];
    for (const id of cursors.slice(1)) selected.delete(id);
    return [keep, ...others];
  };

  const syncDraftIds = () => {
    let ids = enforceCursorRules(order.filter((id) => selected.has(id)));
    if (ids.length > PROFILE_PHOTO_MAX_ITEMS) {
      ids = ids.slice(0, PROFILE_PHOTO_MAX_ITEMS);
      const keep = new Set(ids);
      for (const id of [...selected]) {
        if (!keep.has(id)) selected.delete(id);
      }
    }
    order = [...ids, ...order.filter((id) => !ids.includes(id))];
    draft = normalizeProfilePhotoConfig({ ...draft, cosmeticIds: ids }) || {
      ...draft,
      cosmeticIds: ids,
      cosmeticId: ids[0],
    };
  };

  const redraw = () => {
    syncDraftIds();
    void paintProfilePhoto(
      canvas,
      draft.cosmeticIds.length ? draft : null,
      opts.cosmetics,
      opts.resolveCosmeticDisplay,
      opts.cursorColor,
    );
    refreshCardStates();
  };

  const cardBtns = new Map<string, HTMLButtonElement>();
  const cardEls = new Map<string, HTMLElement>();

  function refreshCardStates() {
    for (const c of cursorCosmetics) {
      const card = cardEls.get(c.id);
      const btn = cardBtns.get(c.id);
      if (!card || !btn) continue;
      const on = selected.has(c.id);
      card.classList.toggle("inventoryCosmeticCard--equipped", on);
      card.classList.toggle("profilePhotoEditorCard--selected", on);
      btn.textContent = on ? "Selected" : "Select";
      btn.classList.toggle("green", !on);
    }
  }

  if (!cursorCosmetics.length) {
    const empty = document.createElement("p");
    empty.className = "accessHint";
    empty.textContent = "No cursor cosmetics owned.";
    grid.appendChild(empty);
  } else {
    for (const c of cursorCosmetics) {
      const card = document.createElement("article");
      card.className = "inventoryCosmeticCard profilePhotoEditorCard";
      card.dataset.cosmeticId = c.id;
      if (isCursorCategory(c)) card.classList.add("profilePhotoEditorCard--cursor");

      const previewWrap = document.createElement("div");
      previewWrap.className = "shopCosmeticCardPreviewWrap";
      const idleCanvas = document.createElement("canvas");
      idleCanvas.className =
        "shopCosmeticCardPreview shopCosmeticCardPreview--idle inventoryCosmeticCardPreview";
      idleCanvas.width = CARD_PREVIEW_SIZE;
      idleCanvas.height = CARD_PREVIEW_SIZE;
      const hoverCanvas = document.createElement("canvas");
      hoverCanvas.className =
        "shopCosmeticCardPreview shopCosmeticCardPreview--hover inventoryCosmeticCardPreview";
      hoverCanvas.width = CARD_PREVIEW_SIZE;
      hoverCanvas.height = CARD_PREVIEW_SIZE;
      hoverCanvas.setAttribute("aria-hidden", "true");
      previewWrap.appendChild(idleCanvas);
      previewWrap.appendChild(hoverCanvas);
      card.appendChild(previewWrap);

      const name = document.createElement("h3");
      name.className = "inventoryCosmeticCardName";
      name.textContent = c.name;
      card.appendChild(name);

      const catHint = document.createElement("p");
      catHint.className = "accessHint";
      catHint.textContent = isCursorCategory(c) ? "Cursor · bottom layer" : c.category || "Cosmetic";
      card.appendChild(catHint);

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn green inventoryCosmeticCardBtn";
      btn.textContent = selected.has(c.id) ? "Selected" : "Select";
      btn.onclick = () => {
        if (selected.has(c.id)) {
          selected.delete(c.id);
        } else {
          if (isCursorCategory(c)) {
            for (const other of cursorCosmetics) {
              if (isCursorCategory(other) && other.id !== c.id) selected.delete(other.id);
            }
            if (!selected.has(c.id) && selected.size >= PROFILE_PHOTO_MAX_ITEMS) {
              status.textContent = `Max ${PROFILE_PHOTO_MAX_ITEMS} items.`;
              return;
            }
            selected.add(c.id);
            order = [c.id, ...order.filter((id) => id !== c.id)];
          } else {
            if (selected.size >= PROFILE_PHOTO_MAX_ITEMS) {
              status.textContent = `Max ${PROFILE_PHOTO_MAX_ITEMS} items.`;
              return;
            }
            selected.add(c.id);
            if (!order.includes(c.id)) order.push(c.id);
          }
        }
        redraw();
      };
      card.appendChild(btn);

      if (!isCursorCategory(c)) {
        const move = document.createElement("div");
        move.className = "profilePhotoEditorItemMove";
        const up = document.createElement("button");
        up.type = "button";
        up.className = "btn profilePhotoEditorMoveBtn";
        up.textContent = "▲";
        up.title = "Lower layer";
        const down = document.createElement("button");
        down.type = "button";
        down.className = "btn profilePhotoEditorMoveBtn";
        down.textContent = "▼";
        down.title = "Higher layer";
        up.onclick = (e) => {
          e.preventDefault();
          if (!selected.has(c.id)) return;
          syncDraftIds();
          const ids = draft.cosmeticIds.filter((id) => !isCursorCategory(byId.get(id)));
          const i = ids.indexOf(c.id);
          if (i <= 0) return;
          [ids[i - 1], ids[i]] = [ids[i], ids[i - 1]];
          const cursorId = draft.cosmeticIds.find((id) => isCursorCategory(byId.get(id)));
          order = cursorId ? [cursorId, ...ids] : ids;
          redraw();
        };
        down.onclick = (e) => {
          e.preventDefault();
          if (!selected.has(c.id)) return;
          syncDraftIds();
          const ids = draft.cosmeticIds.filter((id) => !isCursorCategory(byId.get(id)));
          const i = ids.indexOf(c.id);
          if (i < 0 || i >= ids.length - 1) return;
          [ids[i], ids[i + 1]] = [ids[i + 1], ids[i]];
          const cursorId = draft.cosmeticIds.find((id) => isCursorCategory(byId.get(id)));
          order = cursorId ? [cursorId, ...ids] : ids;
          redraw();
        };
        move.appendChild(up);
        move.appendChild(down);
        card.appendChild(move);
      }

      cardBtns.set(c.id, btn);
      cardEls.set(c.id, card);
      grid.appendChild(card);

      void opts.resolveCosmeticDisplay(c, opts.cursorColor).then(async (layer) => {
        await loadCosmeticImage(layer.imageData);
        if (layer.animationFrames?.length) {
          await Promise.all(layer.animationFrames.map((f) => loadCosmeticImage(f)));
        }
        if (!card.isConnected) return;
        const hideBase = !!c.newCursor;
        paintEditorCard(
          idleCanvas,
          layer.imageData,
          layer.animationFrames,
          opts.cursorColor,
          hideBase,
          false,
        );
        paintEditorCard(
          hoverCanvas,
          layer.imageData,
          layer.animationFrames,
          opts.cursorColor,
          hideBase,
          true,
        );
        if (layer.animationFrames?.length) {
          const startHover = () => {
            startCosmeticPreviewLoop(
              hoverCanvas,
              () =>
                paintEditorCard(
                  hoverCanvas,
                  layer.imageData,
                  layer.animationFrames,
                  opts.cursorColor,
                  hideBase,
                  true,
                ),
              () =>
                card.isConnected &&
                (card.matches(":hover") || card.classList.contains("inventoryCosmeticCard--preview")),
            );
          };
          const stopHover = () => stopCosmeticPreviewLoop(hoverCanvas);
          card.addEventListener("mouseenter", startHover);
          card.addEventListener("mouseleave", stopHover);
          previewWrap.addEventListener("touchstart", () => {
            card.classList.add("inventoryCosmeticCard--preview");
            startHover();
          }, { passive: true });
          previewWrap.addEventListener("touchend", () => {
            card.classList.remove("inventoryCosmeticCard--preview");
            stopHover();
          }, { passive: true });
          previewWrap.addEventListener("touchcancel", () => {
            card.classList.remove("inventoryCosmeticCard--preview");
            stopHover();
          }, { passive: true });
        }
      });
    }
  }

  panel.appendChild(grid);

  const zoomRow = document.createElement("div");
  zoomRow.className = "formRow profilePhotoEditorSliderRow";
  const zoomLabel = document.createElement("span");
  zoomLabel.className = "formLabel";
  zoomLabel.textContent = "Zoom";
  const zoom = document.createElement("input");
  zoom.type = "range";
  zoom.min = String(PROFILE_PHOTO_ZOOM_MIN);
  zoom.max = String(PROFILE_PHOTO_ZOOM_MAX);
  zoom.step = "0.01";
  zoom.value = String(draft.zoom);
  zoom.setAttribute("aria-label", "Zoom");
  zoomRow.appendChild(zoomLabel);
  zoomRow.appendChild(zoom);
  panel.appendChild(zoomRow);

  const rotRow = document.createElement("div");
  rotRow.className = "formRow profilePhotoEditorSliderRow";
  const rotLabel = document.createElement("span");
  rotLabel.className = "formLabel";
  rotLabel.textContent = "Rotate";
  const rot = document.createElement("input");
  rot.type = "range";
  rot.min = "0";
  rot.max = "360";
  rot.step = "1";
  rot.value = String(draft.rotation);
  rot.setAttribute("aria-label", "Rotation");
  rotRow.appendChild(rotLabel);
  rotRow.appendChild(rot);
  panel.appendChild(rotRow);

  const bgRow = document.createElement("div");
  bgRow.className = "formRow profilePhotoEditorSliderRow profilePhotoEditorBgRow";
  const bgLabel = document.createElement("span");
  bgLabel.className = "formLabel";
  bgLabel.textContent = "Background";
  const bgColor = document.createElement("input");
  bgColor.type = "color";
  bgColor.className = "adminShopColorInput profilePhotoEditorBgColor";
  bgColor.value =
    normalizeProfilePhotoBackgroundColor(draft.backgroundColor) || PROFILE_PHOTO_BG_DEFAULT;
  bgColor.setAttribute("aria-label", "Photo background color");
  const syncBannerBtn = document.createElement("button");
  syncBannerBtn.type = "button";
  syncBannerBtn.className = "btn profilePhotoEditorSyncBannerBtn";
  syncBannerBtn.textContent = draft.backgroundFromBanner ? "Synced ✓" : "Match banner";
  syncBannerBtn.title = "Use the most repeated color from your equipped banner";
  bgRow.appendChild(bgLabel);
  bgRow.appendChild(bgColor);
  bgRow.appendChild(syncBannerBtn);
  panel.appendChild(bgRow);

  const syncHint = document.createElement("p");
  syncHint.className = "accessHint";
  syncHint.textContent = draft.backgroundFromBanner
    ? "Background follows your equipped banner (most common color)."
    : "";
  panel.appendChild(syncHint);

  const status = document.createElement("p");
  status.className = "accessHint";
  panel.appendChild(status);

  const btns = document.createElement("div");
  btns.className = "rgbRow";
  btns.style.justifyContent = "flex-end";
  btns.style.gap = "8px";
  const clearBtn = document.createElement("button");
  clearBtn.type = "button";
  clearBtn.className = "btn";
  clearBtn.textContent = "Clear";
  const cancelBtn = document.createElement("button");
  cancelBtn.type = "button";
  cancelBtn.className = "btn";
  cancelBtn.textContent = "Cancel";
  const saveBtn = document.createElement("button");
  saveBtn.type = "button";
  saveBtn.className = "btn green";
  saveBtn.textContent = "Save";
  btns.appendChild(clearBtn);
  btns.appendChild(cancelBtn);
  btns.appendChild(saveBtn);
  panel.appendChild(btns);

  modal.appendChild(panel);
  document.body.appendChild(modal);

  // Normalize initial selection (one cursor, bottom).
  syncDraftIds();
  redraw();

  zoom.oninput = () => {
    draft = {
      ...draft,
      zoom: clamp(Number(zoom.value) || 1, PROFILE_PHOTO_ZOOM_MIN, PROFILE_PHOTO_ZOOM_MAX),
    };
    redraw();
  };
  rot.oninput = () => {
    draft = { ...draft, rotation: ((Number(rot.value) || 0) % 360 + 360) % 360 };
    redraw();
  };

  const setManualBackground = (hex: string) => {
    const normalized = normalizeProfilePhotoBackgroundColor(hex) || PROFILE_PHOTO_BG_DEFAULT;
    draft = {
      ...draft,
      backgroundColor: normalized,
      backgroundFromBanner: false,
    };
    bgColor.value = normalized;
    syncBannerBtn.textContent = "Match banner";
    syncHint.textContent = "";
    redraw();
  };

  bgColor.oninput = () => setManualBackground(bgColor.value);

  syncBannerBtn.onclick = async () => {
    const banner =
      opts.cosmetics.find((c) => c.category === "banner" && c.equipped) || null;
    if (!banner) {
      status.textContent = "Equip a banner on your profile first.";
      return;
    }
    syncBannerBtn.disabled = true;
    status.textContent = "Sampling banner…";
    try {
      const hex = await resolveBannerDominantBackground(
        banner,
        opts.cursorColor,
        opts.resolveCosmeticDisplay,
      );
      if (!hex) {
        status.textContent = "Could not read banner colors.";
        return;
      }
      draft = {
        ...draft,
        backgroundColor: hex,
        backgroundFromBanner: true,
      };
      bgColor.value = hex;
      syncBannerBtn.textContent = "Synced ✓";
      syncHint.textContent =
        "Background follows your equipped banner (most common color).";
      status.textContent = "";
      redraw();
    } finally {
      syncBannerBtn.disabled = false;
    }
  };

  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  const onDown = (clientX: number, clientY: number) => {
    dragging = true;
    lastX = clientX;
    lastY = clientY;
  };
  const onMove = (clientX: number, clientY: number) => {
    if (!dragging) return;
    const rect = canvas.getBoundingClientRect();
    const dx = (clientX - lastX) / Math.max(1, rect.width);
    const dy = (clientY - lastY) / Math.max(1, rect.height);
    lastX = clientX;
    lastY = clientY;
    draft = normalizeProfilePhotoConfig({
      ...draft,
      offsetX: draft.offsetX + dx * 2,
      offsetY: draft.offsetY + dy * 2,
    })!;
    redraw();
  };
  const onUp = () => {
    dragging = false;
  };
  const onWinMove = (e: MouseEvent) => onMove(e.clientX, e.clientY);

  canvas.addEventListener("mousedown", (e) => {
    e.preventDefault();
    onDown(e.clientX, e.clientY);
  });
  window.addEventListener("mousemove", onWinMove);
  window.addEventListener("mouseup", onUp);
  canvas.addEventListener(
    "touchstart",
    (e) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault();
      onDown(t.clientX, t.clientY);
    },
    { passive: false },
  );
  canvas.addEventListener(
    "touchmove",
    (e) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault();
      onMove(t.clientX, t.clientY);
    },
    { passive: false },
  );
  canvas.addEventListener("touchend", onUp);

  const close = () => {
    window.removeEventListener("mousemove", onWinMove);
    window.removeEventListener("mouseup", onUp);
    for (const el of cardEls.values()) {
      const hover = el.querySelector<HTMLCanvasElement>(".shopCosmeticCardPreview--hover");
      if (hover) stopCosmeticPreviewLoop(hover);
    }
    modal.remove();
  };

  cancelBtn.onclick = () => close();
  clearBtn.onclick = async () => {
    clearBtn.disabled = true;
    status.textContent = "Clearing…";
    try {
      const r = await fetch(apiPath("/api/profile"), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ profilePhoto: null }),
      });
      if (!r.ok) throw new Error("save_failed");
      opts.onSaved(null);
      close();
    } catch {
      status.textContent = "Could not clear photo.";
      clearBtn.disabled = false;
    }
  };
  saveBtn.onclick = async () => {
    syncDraftIds();
    if (!draft.cosmeticIds.length) {
      status.textContent = "Pick at least one cosmetic.";
      return;
    }
    saveBtn.disabled = true;
    status.textContent = "Saving…";
    const payload = normalizeProfilePhotoConfig(draft);
    try {
      const r = await fetch(apiPath("/api/profile"), {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ profilePhoto: payload }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(String(err.error || "save_failed"));
      }
      const data = await r.json();
      opts.onSaved(data.profilePhoto ?? payload);
      close();
    } catch (e) {
      status.textContent = e instanceof Error ? e.message : "Save failed.";
      saveBtn.disabled = false;
    }
  };
}
